import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé dans un AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Vérifier si un token existe au chargement
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Configurer axios pour inclure le token dans toutes les requêtes
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // Intercepteur pour gérer les erreurs 401
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // 401 sur les endpoints d'auth = mauvais mot de passe / email,
          // PAS une session expirée. On laisse le composant de login gérer l'erreur.
          const url = error.config?.url || "";
          const isAuthEndpoint = /\/api\/auth\/(login|register|forgot-password|reset-password|reset-password-with-token)/i.test(url);
          if (isAuthEndpoint) {
            return Promise.reject(error);
          }
          // Sinon : vraie session expirée
          console.error("Session expirée, déconnexion...");
          logout();
          window.location.href = "/login";
          alert("⚠️ Votre session a expiré. Veuillez vous reconnecter.");
        }
        return Promise.reject(error);
      }
    );

    // Intercepteur REQUEST : ajoute X-Viewing-Tenant-Id pour l'impersonation Super Admin
    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        const viewingTenantId = localStorage.getItem("viewingTenantId");
        if (viewingTenantId) {
          config.headers["X-Viewing-Tenant-Id"] = viewingTenantId;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    setLoading(false);

    // Cleanup
    return () => {
      axios.interceptors.response.eject(interceptor);
      axios.interceptors.request.eject(reqInterceptor);
    };
  }, []);

  const login = (userData) => {
    setUser(userData);
    const token = localStorage.getItem("token");
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  };

  const logout = () => {
    setUser(null);
    // Clé auth principales
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Contexte impersonation Super Admin — DOIT être purgé sinon la barre "Visualisation" persiste
    localStorage.removeItem("viewingTenantId");
    localStorage.removeItem("viewingTenantName");
    // Headers axios
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-Viewing-Tenant-Id"];
  };

  const isAdmin = () => {
    return user?.role === "admin" || user?.role === "super_admin";
  };

  const isSuperAdmin = () => {
    return user?.role === "super_admin" || user?.is_super_admin === true;
  };

  // Vérifie si l'utilisateur a accès à un module (clé = path sans '/' initial, ex: "planning")
  // Règles: super_admin / admin → toujours true ; allowed_modules=null → toujours true ;
  // allowed_modules=liste → true seulement si moduleKey ∈ liste.
  const canAccess = (moduleKey) => {
    if (!user) return false;
    if (user.role === "admin" || user.role === "super_admin" || user.is_super_admin) return true;
    const allowed = user.allowed_modules;
    if (allowed === null || allowed === undefined) return true;
    return Array.isArray(allowed) && allowed.includes(moduleKey);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isSuperAdmin, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
};
